/**
 * Utility functions for handling Google Drive and external course material URLs.
 * 
 * Solves Google Drive iframe 403 Forbidden and SAMEORIGIN errors by converting:
 * - Folder links (drive.google.com/drive/folders/ID) -> https://drive.google.com/embeddedfolderview?id=ID#grid
 * - File links (drive.google.com/file/d/ID/view) -> https://drive.google.com/file/d/ID/preview
 * - Google Docs/Sheets/Slides -> https://docs.google.com/[type]/d/ID/preview
 * 
 * Also extracts clean direct URLs for external navigation ("Open in Google Drive"),
 * which bypasses third-party cookie restrictions in modern browsers.
 */

export const formatDriveUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;

  const trimmed = rawUrl.trim();
  if (
    !trimmed ||
    trimmed.toLowerCase() === 'null' ||
    trimmed.toLowerCase() === 'undefined' ||
    trimmed.toLowerCase() === 'none' ||
    trimmed === 'N/A'
  ) {
    return null;
  }

  // Already an embedded folder view
  if (trimmed.includes('drive.google.com/embeddedfolderview')) {
    const idMatch = trimmed.match(/id=([a-zA-Z0-9_-]+)/);
    const folderId = idMatch ? idMatch[1] : '';
    return {
      embedUrl: trimmed,
      directUrl: folderId ? `https://drive.google.com/drive/folders/${folderId}` : trimmed,
      type: 'folder',
      isGoogleDrive: true,
      title: 'Google Drive Folder'
    };
  }

  // Google Drive Folder link (/drive/folders/ID or /drive/u/0/folders/ID or ?id=ID)
  const folderMatch = trimmed.match(
    /drive\.google\.com\/(?:drive\/(?:u\/\d+\/)?folders\/|open\?id=)([a-zA-Z0-9_-]+)/
  );
  if (folderMatch) {
    const folderId = folderMatch[1];
    return {
      embedUrl: `https://drive.google.com/embeddedfolderview?id=${folderId}#grid`,
      directUrl: `https://drive.google.com/drive/folders/${folderId}`,
      type: 'folder',
      isGoogleDrive: true,
      title: 'Google Drive Folder'
    };
  }

  // Google Drive File link (/file/d/ID)
  const fileMatch = trimmed.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) {
    const fileId = fileMatch[1];
    return {
      embedUrl: `https://drive.google.com/file/d/${fileId}/preview`,
      directUrl: `https://drive.google.com/file/d/${fileId}/view`,
      type: 'file',
      isGoogleDrive: true,
      title: 'Google Drive Document'
    };
  }

  // Google Docs, Sheets, Slides, Forms
  const docsMatch = trimmed.match(
    /docs\.google\.com\/(document|spreadsheets|presentation|forms)\/d\/([a-zA-Z0-9_-]+)/
  );
  if (docsMatch) {
    const docType = docsMatch[1];
    const docId = docsMatch[2];
    const typeNames = {
      document: 'Google Doc',
      spreadsheets: 'Google Sheet',
      presentation: 'Google Slides',
      forms: 'Google Form'
    };
    return {
      embedUrl: `https://docs.google.com/${docType}/d/${docId}/preview`,
      directUrl: `https://docs.google.com/${docType}/d/${docId}/edit`,
      type: docType,
      isGoogleDrive: true,
      title: typeNames[docType] || 'Google Document'
    };
  }

  // Generic web link (OneDrive, Dropbox, external resources)
  return {
    embedUrl: trimmed,
    directUrl: trimmed,
    type: 'generic',
    isGoogleDrive: false,
    title: 'Resource Link'
  };
};

/**
 * Normalizes a Drive URL before saving to DB
 */
export const normalizeDriveUrlForSave = (rawUrl) => {
  const formatted = formatDriveUrl(rawUrl);
  return formatted ? formatted.embedUrl : null;
};

/**
 * Checks if a string contains a valid Google Drive URL
 */
export const isGoogleDriveUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return /drive\.google\.com|docs\.google\.com/i.test(url);
};
